// src/containers/reportBuilder/index.js
//
// Admin Report Builder — ported from takso-poc/takso-reporting-ui/src/pages/reporting/ReportingPage.jsx.
//
// Owns the filter/browse UI only (sector/org/status/type/manager/search/date
// filters, activity list, checkbox selection). Everything else — tab nav,
// prefs/includedCharts/chartSettings, Charts/Preferences/Graphics tabs, and
// the report preview itself — lives in ReportBuilderPanel, shared with any
// embedded single-activity usage (e.g. the Activity page's "Custom Report"
// slideout). The only thing this file controls that ReportBuilderPanel
// doesn't know about is FIND mode vs PREVIEW mode — handed to the panel via
// the renderReportTab render prop.
//
// FIND mode    — filter panel open, results list, click rows to select
// PREVIEW mode — filter collapses, report preview shown (no tab nav change,
//                just what's shown under the "Report" tab)
//
// Filter panel layout:
//   Row 1: Sector (dropdown) · Organisation (dropdown) · Status (multi-toggle)
//   Row 2: Record Type (segmented) · Manager (segmented) · Search (text)
//   Row 3: Date range (start / end date)

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Dropdown } from 'semantic-ui-react';

import { APP_NAME } from '../../utils/constant';

import {
  getAllOrganisations,
  getReportBuilderActivities,
} from '../../store/actions';

import { reportBuilderGet } from '../../lib/chartApi';
import { LocaleService } from '../../services';

import ReportBuilderPanel from './ReportBuilderPanel';
import ReportPreviewSection, {
  detectTemplateType,
} from './ReportPreviewSection';
import useReportActivityData from './useReportActivityData';

import './ReportBuilder.scss';

// ── Helpers ───────────────────────────────────────────────────────────────────

const dateRange = (s, e) => {
  const a = LocaleService.fmtLocaleDate(s);
  const b = LocaleService.fmtLocaleDate(e);
  if (!a && !b) return null;
  if (!b || a === b) return a;
  return `${a} – ${b}`;
};

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS = {
  Unapproved: { label: 'Development', hex: '#EE7A00' },
  Approved: { label: 'Active', hex: '#50972C' },
  Archived: { label: 'Completed', hex: '#1D3498' },
};

// ── Analysis badge (always grey in this build — no narratives endpoint) ───────

const AnalysisBadge = ({ t }) => (
  <button
    disabled
    title={t('report.builder.no_analysis_run_yet')}
    style={{
      width: 26,
      height: 26,
      borderRadius: '50%',
      background: '#f3f4f6',
      color: '#9ca3af',
      border: 'none',
      cursor: 'default',
      fontSize: 13,
      fontWeight: 700,
      lineHeight: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    –
  </button>
);

// ── Status badge ──────────────────────────────────────────────────────────────

const StatusBadge = ({ statusName }) => {
  const s = STATUS[statusName] || { label: statusName, hex: '#6b7280' };
  return (
    <span className="rb-status-badge" style={{ background: s.hex }}>
      {s.label}
    </span>
  );
};

// ── Activity row ──────────────────────────────────────────────────────────────

const ActivityRow = ({ activity, selected, onToggle }) => {
  const { t } = useTranslation();
  const dr = dateRange(activity.startDate, activity.endDate);
  const accent = STATUS[activity.statusName]?.hex || '#6b7280';

  return (
    <div
      className={`rb-activity-row${
        selected ? ' rb-activity-row--selected' : ''
      }`}
      style={{ borderLeftColor: selected ? '#1d4ed8' : accent }}
      onClick={onToggle}
      role="checkbox"
      aria-checked={selected}
      tabIndex={0}
      onKeyDown={(e) => e.key === ' ' && onToggle()}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        onClick={(e) => e.stopPropagation()}
        className="rb-activity-row__check"
      />

      <div className="rb-activity-row__body">
        <div className="rb-activity-row__top">
          <span
            className={`rb-activity-row__name${
              selected ? ' rb-activity-row__name--selected' : ''
            }`}
          >
            {activity.name}
          </span>
          <StatusBadge statusName={activity.statusName} />
          {activity.isProgram && (
            <span className="rb-chip rb-chip--program">
              {t('report.builder.inputs.program')} ·{' '}
              {t('report.builder.num_linked', {
                count: activity.linkedCount,
                countFmt: LocaleService.fmtLocaleNumeral(activity.linkedCount),
              }).toLowerCase()}
            </span>
          )}
          {!activity.isProgram && activity.parentName && (
            <span className="rb-chip rb-chip--parent">
              ↳ {activity.parentName}
            </span>
          )}
          {!activity.isProgram && !activity.parentName && (
            <span className="rb-chip">{t('activity_type.single')}</span>
          )}
        </div>
        <div className="rb-activity-row__meta">
          <span className="rb-activity-row__id">#{activity.id}</span>
          {activity.activityType && <span> · {activity.activityType}</span>}
          {dr && <span> · {dr}</span>}
          {activity.actualParticipants > 0 && (
            <span>
              {' '}
              ·{' '}
              {t('report.builder.num_participants', {
                count: activity.actualParticipants,
                countFmt: LocaleService.fmtLocaleNumeral(
                  activity.actualParticipants
                ),
              }).toLowerCase()}
            </span>
          )}
        </div>
      </div>

      <AnalysisBadge t={t} />
    </div>
  );
};

// ── Segmented button group ────────────────────────────────────────────────────

const SegmentedGroup = ({ options, value, onChange }) => (
  <div className="rb-segmented">
    {options.map(([v, l], i) => (
      <button
        key={v}
        className={`rb-segmented__btn${
          value === v ? ' rb-segmented__btn--active' : ''
        }`}
        onClick={() => onChange(v)}
      >
        {l}
      </button>
    ))}
  </div>
);

// ── Main container ────────────────────────────────────────────────────────────

export default function AdminReportBuilder() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const orgList = useSelector((s) =>
    Array.isArray(s.admin.organisationsAll) ? s.admin.organisationsAll : []
  );
  const activities = useSelector((s) => s.reportBuilder.activities);
  const loading = useSelector((s) => s.reportBuilder.activitiesLoading);

  // Sector & org selection
  const [sectors, setSectors] = useState([]);
  const [selectedSectorId, setSelectedSectorId] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState(new Set());
  const [typeFilter, setTypeFilter] = useState('all'); // all|program|single|linked
  const [managerFilter, setManagerFilter] = useState('all'); // all|staff|guest
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchInput, setSearchInput] = useState(''); // raw — updates on every keystroke
  const [searchText, setSearchText] = useState(''); // debounced — used for filtering

  useEffect(() => {
    const timer = setTimeout(() => setSearchText(searchInput), 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Selection (ordered array of IDs)
  const [selectedIds, setSelectedIds] = useState([]);

  // View mode
  const [filterOpen, setFilterOpen] = useState(true);

  // ── Bootstrap ───────────────────────────────────────────────────────────────

  useEffect(() => {
    document.title = `${t('report.builder.title')} | ${APP_NAME}`;
    dispatch(getAllOrganisations());
    reportBuilderGet('sector-list')
      .then((j) => {
        if (j?.sectors) setSectors(j.sectors);
      })
      .catch(() => {});
  }, [dispatch]);

  // ── Activity list: reload when org changes ───────────────────────────────────

  useEffect(() => {
    if (!selectedOrgId) return;
    setSelectedIds([]);
    dispatch(getReportBuilderActivities(Number(selectedOrgId)));
  }, [selectedOrgId, dispatch]);

  // ── Sector-filtered org list ─────────────────────────────────────────────────

  const visibleOrgs = useMemo(() => {
    if (!selectedSectorId) return orgList;
    return orgList.filter(
      (o) => String(o.industrySectorTypeId) === selectedSectorId
    );
  }, [orgList, selectedSectorId]);

  // ── Client-side filters ──────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let list = activities;

    if (statusFilter.size > 0)
      list = list.filter((a) => statusFilter.has(a.statusName));

    if (typeFilter === 'program') list = list.filter((a) => a.isProgram);
    if (typeFilter === 'single')
      list = list.filter((a) => !a.isProgram && !a.parentId);
    if (typeFilter === 'linked')
      list = list.filter((a) => !a.isProgram && !!a.parentId);

    if (managerFilter === 'staff') list = list.filter((a) => !a.isGuestManager);
    if (managerFilter === 'guest') list = list.filter((a) => a.isGuestManager);

    if (searchText.trim()) {
      const q = searchText.trim();
      const asId = parseInt(q, 10);
      const isInteger = !isNaN(asId) && String(asId) === q;
      list = list.filter(
        (a) =>
          a.name.toLowerCase().includes(q.toLowerCase()) ||
          (isInteger && a.id === asId)
      );
    }

    if (startDate)
      list = list.filter((a) => !a.endDate || a.endDate >= startDate);
    if (endDate)
      list = list.filter((a) => !a.startDate || a.startDate <= endDate);

    return list;
  }, [
    activities,
    statusFilter,
    typeFilter,
    managerFilter,
    searchText,
    startDate,
    endDate,
  ]);

  // Keep natural filter order — reordering to top causes scroll jump on selection
  const sortedList = filtered;

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const toggleSelect = useCallback((activity) => {
    setSelectedIds((prev) => {
      const has = prev.includes(activity.id);
      return has
        ? prev.filter((id) => id !== activity.id)
        : [...prev, activity.id];
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  const toggleStatus = useCallback((s) => {
    setStatusFilter((prev) => {
      const n = new Set(prev);
      n.has(s) ? n.delete(s) : n.add(s);
      return n;
    });
  }, []);

  // Clears only the activity-list filters (status, type, manager, search, dates).
  // Called internally when org/sector changes so stale results are not shown.
  const clearActivityFilters = useCallback(() => {
    setStatusFilter(new Set());
    setTypeFilter('all');
    setManagerFilter('all');
    setSearchInput('');
    setSearchText('');
    setStartDate('');
    setEndDate('');
  }, []);

  // Clears everything — org, sector, and all activity-list filters.
  // Called by the "Clear filters" button.
  const clearFilters = useCallback(() => {
    setSelectedSectorId('');
    setSelectedOrgId('');
    clearActivityFilters();
  }, [clearActivityFilters]);

  const hasFilters =
    selectedSectorId ||
    selectedOrgId ||
    statusFilter.size > 0 ||
    typeFilter !== 'all' ||
    managerFilter !== 'all' ||
    searchInput ||
    startDate ||
    endDate;

  // ── Derived values ────────────────────────────────────────────────────────────

  const selectedActivities = useMemo(
    () =>
      selectedIds
        .map((id) => activities.find((a) => a.id === id))
        .filter(Boolean),
    [selectedIds, activities]
  );

  const selectedOrg =
    visibleOrgs.find((o) => String(o.id) === selectedOrgId) || null;
  const orgName = selectedOrg?.name || '';

  const templateType = detectTemplateType(selectedActivities);

  const programCount = filtered.filter((a) => a.isProgram).length;
  const singleCount = filtered.filter(
    (a) => !a.isProgram && !a.parentId
  ).length;
  const linkedCount = filtered.filter(
    (a) => !a.isProgram && !!a.parentId
  ).length;

  const inPreview = !filterOpen && selectedActivities.length > 0;

  // Per-activity detail data — shared between the Report tab's preview and
  // the Charts tab (evaluationPlans, chartImages), owned here so both the
  // renderReportTab callback below and ReportBuilderPanel's ChartsTab can
  // read it.
  const orgIdNum = selectedOrgId ? Number(selectedOrgId) : null;
  const activityData = useReportActivityData(selectedActivities);

  // ── Main render ───────────────────────────────────────────────────────────────

  return (
    <>
      <ReportBuilderPanel
        orgId={orgIdNum}
        orgName={orgName}
        selectedActivities={selectedActivities}
        chartFilters={{
          statuses: [...statusFilter],
          type: typeFilter,
          manager: managerFilter,
          startDate,
          endDate,
        }}
        evaluationPlans={activityData.evaluationPlans}
        chartImages={activityData.chartImages}
        onChartImageChange={activityData.handleChartImageChange}
        renderReportTab={({
          prefs,
          includedCharts,
          chartSettings,
          assistPrefs,
          reportAssistEnabled,
        }) =>
          inPreview ? (
            <ReportPreviewSection
              selectedActivities={selectedActivities}
              orgId={orgIdNum}
              orgName={orgName}
              prefs={prefs}
              includedCharts={includedCharts}
              chartSettings={chartSettings}
              assistPrefs={assistPrefs}
              reportAssistEnabled={reportAssistEnabled}
              venues={activityData.venues}
              objectives={activityData.objectives}
              crossRefs={activityData.crossRefs}
              fastFacts={activityData.fastFacts}
              evaluationPlans={activityData.evaluationPlans}
              chartImages={activityData.chartImages}
              shortlistedComments={activityData.shortlistedComments}
              surveyResponseComments={activityData.surveyResponseComments}
              onEdit={() => setFilterOpen(true)}
              onClear={() => {
                clearSelection();
                setFilterOpen(true);
              }}
            />
          ) : (
            <>
              {/* ── Filter panel ─────────────────────────────────────────────────────── */}
              <div className="rb-filter-panel">
                {/* Row 1: Sector + Org + Status */}
                <div className="rb-filter-panel__row">
                  {/* Sector */}
                  <div className="rb-filter-field rb-filter-field--sm">
                    <label className="rb-label">{t('search.sector')}</label>
                    <select
                      className="rb-select"
                      value={selectedSectorId}
                      onChange={(e) => {
                        setSelectedSectorId(e.target.value);
                        setSelectedOrgId('');
                        clearActivityFilters();
                        clearSelection();
                      }}
                    >
                      <option value="">{t('search.all_sectors')}</option>
                      {sectors.map((s) => (
                        <option key={s.id} value={String(s.id)}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Organisation */}
                  <div className="rb-filter-field rb-filter-field--lg">
                    <label className="rb-label">
                      {t('common.organisation')}
                    </label>
                    <Dropdown
                      placeholder={`${t('formulary.placeholder.select_organisation')}…`}
                      fluid
                      search
                      selection
                      clearable
                      value={selectedOrgId || ''}
                      options={visibleOrgs.map((o) => ({
                        key: o.id,
                        value: String(o.id),
                        text: o.name,
                      }))}
                      loading={orgList.length === 0}
                      onChange={(e, { value }) => {
                        setSelectedOrgId(value || '');
                        clearActivityFilters();
                        clearSelection();
                        // Auto-populate sector when an org is selected
                        if (value) {
                          const org = visibleOrgs.find(
                            (o) => String(o.id) === value
                          );
                          if (org?.industrySectorTypeId) {
                            setSelectedSectorId(
                              String(org.industrySectorTypeId)
                            );
                          }
                        }
                      }}
                      className="rb-org-dropdown"
                    />
                  </div>

                  {/* Status multi-toggle */}
                  <div className="rb-filter-field">
                    <label className="rb-label">
                      {t('common.status')}
                      {statusFilter.size === 0 && (
                        <span className="rb-label__sub">
                          {' '}
                          ({t('common.all').toLowerCase()})
                        </span>
                      )}
                    </label>
                    <div className="rb-status-toggles">
                      {Object.entries(STATUS).map(([key, { label, hex }]) => {
                        const on = statusFilter.has(key);
                        return (
                          <button
                            key={key}
                            onClick={() => toggleStatus(key)}
                            className="rb-status-toggle"
                            style={{
                              borderColor: on ? hex : '#d1d5db',
                              background: on ? hex : '#fff',
                              color: on ? '#fff' : hex,
                              fontWeight: on ? 700 : 500,
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Row 2: Record Type + Manager + Search */}
                <div className="rb-filter-panel__row">
                  {/* Record Type */}
                  <div className="rb-filter-field">
                    <label className="rb-label">
                      {t('search.record_type')}
                    </label>
                    <SegmentedGroup
                      value={typeFilter}
                      onChange={setTypeFilter}
                      options={[
                        ['all', 'All'],
                        ['program', 'Program'],
                        ['single', 'Single'],
                        ['linked', 'Linked'],
                      ]}
                    />
                  </div>

                  {/* Manager */}
                  <div className="rb-filter-field">
                    <label className="rb-label">{t('common.manager')}</label>
                    <SegmentedGroup
                      value={managerFilter}
                      onChange={setManagerFilter}
                      options={[
                        ['all', 'All'],
                        ['staff', 'Staff'],
                        ['guest', 'Guest'],
                      ]}
                    />
                  </div>

                  {/* Search */}
                  <div className="rb-filter-field rb-filter-field--search">
                    <label className="rb-label">{t('actions.search')}</label>
                    <div className="rb-search-wrap">
                      <input
                        type="text"
                        placeholder={`${t('common.name')}…`}
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="rb-search-input"
                      />
                      {searchInput && (
                        <button
                          onClick={() => {
                            setSearchInput('');
                            setSearchText('');
                          }}
                          className="rb-search-clear"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Row 3: Date range */}
                <div className="rb-filter-panel__row rb-filter-panel__row--date">
                  <div className="rb-filter-field">
                    <label className="rb-label">
                      {t('report.builder.activity_date_range')}{' '}
                      <span className="rb-label__sub">
                        ({t('common.optional')}.toLowerCase())
                      </span>
                    </label>
                    <div className="rb-date-range">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="rb-date-input"
                      />
                      <span className="rb-date-range__sep">
                        {t('actions.to')}
                      </span>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="rb-date-input"
                      />
                      {(startDate || endDate) && (
                        <button
                          onClick={() => {
                            setStartDate('');
                            setEndDate('');
                          }}
                          className="rb-clear-link"
                        >
                          {t('actions.clear')}
                        </button>
                      )}
                    </div>
                  </div>

                  <div style={{ flex: 1 }} />

                  <button
                    onClick={clearFilters}
                    disabled={!hasFilters}
                    className={`rb-clear-btn${
                      hasFilters ? '' : ' rb-clear-btn--inactive'
                    }`}
                  >
                    {t('search.clear_filters')}
                  </button>
                </div>
              </div>

              {/* ── Results area ──────────────────────────────────────────────────────── */}

              {!selectedOrgId ? (
                <div className="rb-empty rb-empty--centered">
                  {t('report.builder.select_org_to_view_activities')}
                </div>
              ) : loading ? (
                <div className="rb-loading">
                  <div
                    className="spinner-border spinner-border-sm text-secondary"
                    role="status"
                  />
                  <span>{t('report.builder.loading_activities')}…</span>
                </div>
              ) : (
                <>
                  {/* Summary + action bar */}
                  <div className="rb-results-bar">
                    <span className="rb-count">
                      {sortedList.length === 0
                        ? t(
                            'report.builder.no_activities_match_current_filters'
                          )
                        : [
                            programCount > 0 &&
                              t('report.builder.program_cnt', {
                                count: programCount,
                              }).toLowerCase(),
                            singleCount > 0 &&
                              t('report.builder.single_cnt', {
                                count: singleCount,
                              }).toLowerCase(),
                            linkedCount > 0 &&
                              t('report.builder.linked_cnt', {
                                count: linkedCount,
                              }).toLowerCase(),
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                      {selectedIds.length > 0 && (
                        <span className="rb-count__selected">
                          {' '}
                          ·{' '}
                          {t('report.builder.selected_cnt', {
                            count: selectedIds.length,
                          }).toLowerCase()}
                        </span>
                      )}
                    </span>

                    <div className="rb-results-bar__actions">
                      {selectedIds.length > 0 && (
                        <button
                          onClick={clearSelection}
                          className="rb-clear-link"
                        >
                          {t('formulary.clear_selection')}
                        </button>
                      )}
                      <button
                        onClick={() => setFilterOpen(false)}
                        disabled={selectedIds.length === 0}
                        className={`rb-btn rb-btn--primary${
                          selectedIds.length === 0 ? ' rb-btn--disabled' : ''
                        }`}
                      >
                        {t('admin.system_reports.preview_report')} →
                      </button>
                    </div>
                  </div>

                  {/* Activity list */}
                  {sortedList.length > 0 && (
                    <div className="rb-list">
                      {sortedList.map((a) => (
                        <ActivityRow
                          key={a.id}
                          activity={a}
                          selected={selectedIds.includes(a.id)}
                          onToggle={() => toggleSelect(a)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )
        }
      />

      {/* ── Selection bar (sticky — shown on all tabs) ───────────────────────── */}
      {selectedIds.length > 0 && (
        <div className="rb-selection-bar">
          <div className="rb-selection-bar__left">
            <span className="rb-selection-bar__count">
              {selectedIds.length} {t('common.selected')}
            </span>
            {templateType && (
              <span className="rb-selection-bar__type">
                ·{' '}
                {templateType === 'multi-program'
                  ? t('report.builder.report_types.multi_program_report')
                  : templateType === 'program'
                    ? t('report.builder.report_types.program_report')
                    : templateType === 'single'
                      ? t('report.builder.report_types.single_activity_report')
                      : templateType === 'multi-single'
                        ? t('report.builder.report_types.multi_single_report')
                        : t('report.builder.report_types.mixed_report')}
              </span>
            )}
          </div>
          <div className="rb-selection-bar__actions">
            <button className="rb-btn rb-btn--ghost" onClick={clearSelection}>
              {t('actions.clear')}
            </button>
            <button
              className="rb-btn rb-btn--primary"
              onClick={() => setFilterOpen(false)}
            >
              {t('report.builder.preview_report')} →
            </button>
          </div>
        </div>
      )}
    </>
  );
}
